import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/branding.config";

/**
 * Blocking overlay shown when the current user has `must_change_password = true`.
 * Occupies the full viewport, cannot be dismissed until a new password is set.
 */
export default function ForcePasswordChange() {
  const { user, forceChangePassword, logout } = useAuth();
  const [pw, setPw]   = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2)    return setErr("Passwords do not match.");
    setBusy(true);
    const res = await forceChangePassword(pw);
    setBusy(false);
    if (res.ok) {
      toast.success("Password updated. Welcome!");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-center justify-center p-6"
      data-testid="force-password-change-overlay"
    >
      <div className="glass-strong max-w-md w-full p-8 md:p-10 relative overflow-hidden">
        <div className="orb bg-violet w-[280px] h-[280px] -left-20 -top-20 opacity-40" aria-hidden />
        <div className="orb bg-pink w-[220px] h-[220px] -right-16 -bottom-16 opacity-30" aria-hidden />
        <div className="relative">
          <div className="h-11 w-11 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <KeyRound size={18} className="text-primary" />
          </div>
          <h2 className="mt-5 text-2xl font-black tracking-tight">Set a new password</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            You're signed in with a temporary password. Choose a new one to unlock the {BRAND.app_name} console.
          </p>

          {user?.email && (
            <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck size={14} className="text-primary" />
              Signed in as <span className="font-mono text-foreground">{user.email}</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">New password</label>
              <input
                type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8}
                data-testid="force-password-new"
                placeholder="••••••••"
                className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Confirm password</label>
              <input
                type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8}
                data-testid="force-password-confirm"
                placeholder="••••••••"
                className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
            </div>
            {err && (
              <div data-testid="force-password-error" className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {err}
              </div>
            )}
            <button
              type="submit" disabled={busy}
              data-testid="force-password-submit"
              className="btn-primary w-full py-3 text-sm"
            >
              {busy ? "Saving…" : "Update password"}
            </button>
            <button
              type="button" onClick={logout}
              data-testid="force-password-logout"
              className="btn-ghost w-full py-2 text-xs text-muted-foreground"
            >
              Sign out
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
